import { BaseBuilder, BaseBuilderOptions, IBuilder } from './BaseBuilder.ts';
import { ProductCriteriaOptions } from '@services/MarppleShop/features/ProductList/type/product-list.request.ts';
import { CommonPageType, CommonRankType, CommonFilters } from './shared/common-types';
import { PageManager, RankManager, FilterManager, SortManager } from './managers';

export type HandmadePage = CommonPageType;
export type HandmadeRankType = CommonRankType;
export type HandmadeBuilderFilters = CommonFilters;

export interface HandmadeBuilderOptions extends BaseBuilderOptions {
  page?: HandmadePage;
  filters?: HandmadeBuilderFilters;
  sort?: ProductCriteriaOptions['sort'];
  rankType?: HandmadeRankType;
}

export class HandmadeBuilder implements IBuilder {
  private baseBuilder: BaseBuilder;

  private pageManager: PageManager<HandmadePage>;
  private rankManager: RankManager<HandmadeRankType>;
  private filterManager: FilterManager<HandmadeBuilderFilters>;
  private sortManager: SortManager;

  // string 타입 기반 URL 매핑
  private static readonly PAGE_URL_MAP: Record<HandmadePage, string> = {
    RANKING: '/:lang/@/hand-made/ranking',
    PRODUCTS: '/:lang/@/hand-made/products',
  };

  constructor(initialOptions: HandmadeBuilderOptions = {}) {
    this.baseBuilder = new BaseBuilder(initialOptions);

    this.pageManager = new PageManager(initialOptions.page, 'RANKING', HandmadeBuilder.PAGE_URL_MAP);

    this.rankManager = new RankManager(initialOptions.rankType, 'REALTIME');

    this.filterManager = new FilterManager(initialOptions.filters, {});

    this.sortManager = new SortManager(initialOptions.sort, 'popular');
  }

  private getBaseUrl(): string {
    const lang = this.baseBuilder.getLangOrThrow();
    return this.pageManager.getPageUrl(lang);
  }

  setPage(page: HandmadePage): this {
    this.pageManager.setPage(page);
    return this;
  }

  setRankType(rankType: HandmadeRankType): this {
    this.rankManager.setRankType(rankType);
    return this;
  }

  setFilters(filters: Partial<HandmadeBuilderFilters>): this {
    this.filterManager.setFilters(filters);
    return this;
  }

  setSort(sort: ProductCriteriaOptions['sort']): this {
    this.sortManager.setSort(sort);
    return this;
  }

  build(): string {
    const baseUrl = this.getBaseUrl();

    if (this.pageManager.isRanking()) {
      return this.rankManager.buildRankingUrl(baseUrl);
    } else if (this.pageManager.isProducts()) {
      return this.filterManager.buildProductsUrl(baseUrl, this.sortManager.getSort());
    } else {
      return baseUrl;
    }
  }

  clone(): HandmadeBuilder {
    return new HandmadeBuilder({
      lang: this.baseBuilder.getLangOrThrow(),
      page: this.pageManager.getPage(),
      filters: this.filterManager.getFilters(),
      sort: this.sortManager.getSort(),
      rankType: this.rankManager.getRankType(),
    });
  }
}
